///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Who felt it: estimated shaking intensity at nearby cities from the
/// local `locations` table, using the Allen, Wald & Worden (2012) intensity
/// prediction equation for active crust with distance measured to the
/// rupture (a surface segment of Wells & Coppersmith length along the
/// strike through the epicenter, combined with the hypocentral depth), not
/// to the epicenter point. PAGER-style exposure, computed in the browser.
///

import { LocationsTable } from "@/earthbrowser/dataset-distribution/locations/LocationsTable"
import { localOffsetKm, ruptureLengthKm } from "./cutoutLayout"

const cKmPerDegree = 111.32
const cMinCityPopulation = 5000
const cMaxCities = 4000
/** Lowest intensity worth listing (MMI IV, "light") */
const cMinMmi = 4
const cTopCities = 5

export interface ExposedCity {
	name: string
	country: string
	population: number
	mmi: number
	distanceKm: number
}

export interface ExposureSummary {
	model: string
	/** Rupture-distance radius inside which MMI ≥ IV is predicted, km */
	radiusKm: number
	/** Population of listed cities at or above each intensity (cumulative), index = MMI */
	populationAtOrAbove: Record<number, number>
	/** Strongest shaking first */
	cities: ExposedCity[]
	cityCount: number
}

/**
 * Allen et al. (2012) IPE, rupture-distance form:
 * I = c0 + c1 M + c2 ln( sqrt(Rrup² + (1 + c3 e^(M−5))²) )
 */
export function predictedMmi(mag: number, rruptKm: number): number {
	const c0 = 3.950, c1 = 0.913, c2 = -1.107, c3 = 0.813
	const rm = 1 + c3 * Math.exp(mag - 5)
	return c0 + c1 * mag + c2 * Math.log(Math.sqrt(rruptKm * rruptKm + rm * rm))
}

/** Rupture distance at which the predicted intensity falls to `mmi` */
export function radiusForMmi(mag: number, mmi: number): number {
	let lo = 1, hi = 2000
	if (predictedMmi(mag, hi) >= mmi) return hi
	if (predictedMmi(mag, lo) < mmi) return lo
	for (let i = 0; i < 40; i++) {
		const mid = (lo + hi) / 2
		if (predictedMmi(mag, mid) >= mmi) lo = mid
		else hi = mid
	}
	return hi
}

export class ExposureQuery {
	/**
	 * @param strikeDeg - strike of the rupture segment (chosen nodal plane, aftershock trend or block strike)
	 */
	public static async load(lat0: number, lon0: number, depthKm: number, mag: number, strikeDeg: number): Promise<ExposureSummary | undefined> {
		const radiusKm = Math.min(1500, radiusForMmi(mag, cMinMmi))
		const halfLen = ruptureLengthKm(mag) / 2
		const reach = radiusKm + halfLen
		const dLat = reach / cKmPerDegree
		const dLon = reach / (cKmPerDegree * Math.max(Math.cos(lat0 * Math.PI / 180), 0.05))
		const rows = await LocationsTable.queryByBBox(lon0 - dLon, lat0 - dLat, lon0 + dLon, lat0 + dLat, cMaxCities, undefined, "high")
		if (rows.length === 0) return undefined

		// Surface projection of the rupture: a segment along strike through the epicenter
		const s = strikeDeg * Math.PI / 180
		const ux = Math.sin(s), uy = Math.cos(s)	// east, north

		const cities: ExposedCity[] = []
		const populationAtOrAbove: Record<number, number> = {}
		for (const row of rows) {
			if (row.pop < cMinCityPopulation) continue
			const local = localOffsetKm(row.lat, row.lon, lat0, lon0)
			// Distance from the city to the segment (Joyner-Boore), then to the rupture at depth
			const along = Math.max(-halfLen, Math.min(halfLen, local.east * ux + local.north * uy))
			const px = along * ux, py = along * uy
			const rjb = Math.hypot(local.east - px, local.north - py)
			const rrup = Math.hypot(rjb, depthKm)
			const mmi = predictedMmi(mag, rrup)
			if (mmi < cMinMmi) continue
			cities.push({ name: row.name, country: row.country, population: row.pop, mmi, distanceKm: Math.hypot(local.east, local.north) })
			for (let level = cMinMmi; level <= Math.floor(mmi + 0.5); level++) {
				populationAtOrAbove[level] = (populationAtOrAbove[level] ?? 0) + row.pop
			}
		}
		if (cities.length === 0) return undefined
		cities.sort((a, b) => b.mmi - a.mmi || b.population - a.population)
		return {
			model: "Allen, Wald & Worden (2012)",
			radiusKm,
			populationAtOrAbove,
			cities: cities.slice(0, cTopCities),
			cityCount: cities.length
		}
	}
}
